/**
 * Client-side encryption utilities using Web Crypto API (AES-256-GCM).
 *
 * Every encrypted value is a base64 string containing [12-byte IV | ciphertext].
 * The IV is randomly generated per encrypt call for semantic security.
 */

// --- Key management ---

export const generateEncryptionKey = async (): Promise<CryptoKey> => {
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true, // extractable — needed for export to Convex
    ['encrypt', 'decrypt']
  );
};

export const exportKey = async (key: CryptoKey): Promise<string> => {
  const raw = await crypto.subtle.exportKey('raw', key);
  return uint8ToBase64(new Uint8Array(raw));
};

export const importKey = async (base64Key: string): Promise<CryptoKey> => {
  const raw = base64ToUint8(base64Key);
  return crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
};

// --- Recovery key (private / E2EE mode) ---

/**
 * Generate a high-entropy recovery key, formatted as groups of Crockford base32
 * for readability, e.g. "XB2K-9F4Q-7T1M-3PD8-VR6N-K2WL". 30 chars ≈ 150 bits.
 */
const RECOVERY_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32 (no I, L, O, U)

export const generateRecoveryKey = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += RECOVERY_ALPHABET[bytes[i] % RECOVERY_ALPHABET.length];
    if ((i + 1) % 4 === 0 && i < bytes.length - 1) out += '-';
  }
  return out;
};

/** Normalize user-entered recovery keys (strip spaces/dashes, uppercase). */
export const normalizeRecoveryKey = (raw: string): string =>
  raw.replace(/[\s-]/g, '').toUpperCase();

/** Generate a random base64 salt for recovery key derivation. */
export const generateRecoverySalt = (): string =>
  uint8ToBase64(crypto.getRandomValues(new Uint8Array(16)));

const PBKDF2_ITERATIONS = 310_000;

/** Derive a key-encrypting key (KEK) from a recovery key + salt via PBKDF2. */
export const deriveKekFromRecovery = async (
  recoveryKey: string,
  saltBase64: string
): Promise<CryptoKey> => {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(normalizeRecoveryKey(recoveryKey)),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: base64ToUint8(saltBase64),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
};

/** Wrap (encrypt) a data key under a KEK. Returns base64 of [IV | ciphertext]. */
export const wrapDataKey = async (
  dataKey: CryptoKey,
  kek: CryptoKey
): Promise<string> => {
  const exported = await exportKey(dataKey);
  return encrypt(exported, kek);
};

/** Unwrap (decrypt) a data key wrapped under a KEK. Throws if the KEK is wrong. */
export const unwrapDataKey = async (
  wrappedBase64: string,
  kek: CryptoKey
): Promise<CryptoKey> => {
  const exported = await decrypt(wrappedBase64, kek);
  return importKey(exported);
};

// --- Encrypt / Decrypt ---

export const encrypt = async (
  plaintext: string,
  key: CryptoKey
): Promise<string> => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded
  );
  // Prepend IV to ciphertext
  const combined = new Uint8Array(iv.length + new Uint8Array(ciphertext).length);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return uint8ToBase64(combined);
};

export const decrypt = async (
  base64Ciphertext: string,
  key: CryptoKey
): Promise<string> => {
  const combined = base64ToUint8(base64Ciphertext);
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );
  return new TextDecoder().decode(decrypted);
};

// --- Base64 helpers ---

const uint8ToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToUint8 = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

// --- Entry encryption ---

/** Fields in an entry that get encrypted. */
interface RawEntryFields {
  date: string;
  notes: string;
  images?: string[];
  stickerImages?: string[];
  severityRatings?: Array<{ category: string; value: number }>;
}

/** After encryption: severityRatings becomes a single encrypted string. */
interface EncryptedEntryFields {
  date: string;           // ciphertext
  notes: string;          // ciphertext
  images?: string[];      // each element ciphertext
  stickerImages?: string[]; // each element ciphertext
  severityRatings?: string; // encrypted JSON string
}

export const encryptEntry = async (
  entry: RawEntryFields,
  key: CryptoKey
): Promise<EncryptedEntryFields> => {
  const [encDate, encNotes] = await Promise.all([
    encrypt(entry.date, key),
    encrypt(entry.notes, key),
  ]);

  const encImages = entry.images
    ? await Promise.all(entry.images.map((img) => encrypt(img, key)))
    : undefined;

  const encStickers = entry.stickerImages
    ? await Promise.all(entry.stickerImages.map((img) => encrypt(img, key)))
    : undefined;

  const encSeverity = entry.severityRatings
    ? await encrypt(JSON.stringify(entry.severityRatings), key)
    : undefined;

  return {
    date: encDate,
    notes: encNotes,
    images: encImages,
    stickerImages: encStickers,
    severityRatings: encSeverity,
  };
};

export const decryptEntry = async (
  entry: EncryptedEntryFields,
  key: CryptoKey
): Promise<RawEntryFields> => {
  const [date, notes] = await Promise.all([
    decrypt(entry.date, key),
    decrypt(entry.notes, key),
  ]);

  const images = entry.images
    ? await Promise.all(entry.images.map((img) => decrypt(img, key)))
    : undefined;

  const stickerImages = entry.stickerImages
    ? await Promise.all(entry.stickerImages.map((img) => decrypt(img, key)))
    : undefined;

  const severityRatings = entry.severityRatings
    ? JSON.parse(await decrypt(entry.severityRatings, key))
    : undefined;

  return { date, notes, images, stickerImages, severityRatings };
};

// --- Chat message encryption ---

interface ChatMessageFields {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  images?: string[];
}

export const encryptChatMessages = async (
  messages: ChatMessageFields[],
  key: CryptoKey
): Promise<ChatMessageFields[]> => {
  return Promise.all(
    messages.map(async (msg) => ({
      ...msg,
      content: await encrypt(msg.content, key),
      images: msg.images
        ? await Promise.all(msg.images.map((img) => encrypt(img, key)))
        : undefined,
    }))
  );
};

export const decryptChatMessages = async (
  messages: ChatMessageFields[],
  key: CryptoKey
): Promise<ChatMessageFields[]> => {
  return Promise.all(
    messages.map(async (msg) => ({
      ...msg,
      content: await decrypt(msg.content, key),
      images: msg.images
        ? await Promise.all(msg.images.map((img) => decrypt(img, key)))
        : undefined,
    }))
  );
};
